/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Stack, Typography } from "@mui/material";
import { AvailableScrapItemConfigFactory } from "components/search/input/DifferencePanel/AvailableScrapItemConfigFactory";
import { DiffCategory } from "components/search/input/DifferencePanel/common";
import { PeriodParamsConfigFactory } from "components/search/input/DifferencePanel/PeriodParamsConfigFactory";
import { ProductionGroupConfigFactory } from "components/search/input/DifferencePanel/ProductionGroupConfigFactory";
import { ProductionItemConfigFactory } from "components/search/input/DifferencePanel/ProductionItemConfigFactory";
import { TargetBasketItemConfigFactory } from "components/search/input/DifferencePanel/TargetBasketItemConfigFactory";
import { TargetInventoryItemConfigFactory } from "components/search/input/DifferencePanel/TargetInventoryItemConfigFactory";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useIsAdmin } from "hooks/settings";
import { useMemo } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { NameBasedSearchPeriodDiff } from "store/api/generatedApi";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Stack flexShrink={0}>
      <Typography variant="caption" fontWeight="bold">
        {title}
      </Typography>
      <Stack
        sx={{
          "&>*": {
            borderBottom: 1,
            borderColor: "divider",
            "&:last-child": {
              borderBottomWidth: 0,
              pb: 1,
            },
          },
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};

const PeriodHeader = ({
  name,
  status,
}: {
  name: string | null;
  status: "added" | "removed" | "unchanged";
}) => {
  const { t } = useTenantTranslation();
  const color = {
    added: "success.main",
    removed: "error.main",
    unchanged: undefined,
  };
  const prefix = {
    added: "+ ",
    removed: "- ",
    unchanged: "",
  };
  return (
    <Typography variant="h6" fontWeight="bold" color={color[status]}>
      {prefix[status]}
      {name ?? `${t("forecast")} (${t("unnamedPeriod")})`}
    </Typography>
  );
};

export const DifferencePanelPeriod = ({
  periodDiff,
}: {
  periodDiff: NameBasedSearchPeriodDiff;
}) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const formatNumber = useFormatNumber();
  const formatNumber2DP = formatNumber(2);
  const formatNumber2DPFixed = formatNumber(2, true);
  const isAdmin = useIsAdmin();

  const [
    availableScrapAttributesConfig,
    targetBasketAttributesConfig,
    targetInventoryAttributesConfig,
    productionGroupAttributesConfig,
    productionItemAttributesConfig,
    periodParamsAttributesConfig,
  ] = useMemo(() => {
    const utils = {
      t,
      units,
      formatNumber2DP,
      formatNumber2DPFixed,
      isAdmin,
    };
    return [
      AvailableScrapItemConfigFactory(utils),
      TargetBasketItemConfigFactory(utils),
      TargetInventoryItemConfigFactory(utils),
      ProductionGroupConfigFactory(utils),
      ProductionItemConfigFactory(utils),
      PeriodParamsConfigFactory(utils),
    ];
  }, [t, units, formatNumber2DP, formatNumber2DPFixed, isAdmin]);

  const hasAnyDifference = periodDiff
    ? Object.entries(periodDiff).some(([key, value]) => {
        if (key === "name") return false;
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined && value !== null;
      })
    : false;

  if (!hasAnyDifference) return null;

  if (
    periodDiff.is_present?.updated === false &&
    periodDiff.is_present.previous === true
  ) {
    // period was removed
    return <PeriodHeader name={periodDiff.name} status="removed" />;
  }

  return (
    <Stack gap={2}>
      <PeriodHeader
        name={periodDiff.name}
        status={periodDiff.is_present?.updated ? "added" : "unchanged"}
      />
      {periodDiff.period_params ? (
        <Section title={t("period")}>
          <DiffCategory
            hideKeys
            diff={periodDiff.period_params}
            config={periodParamsAttributesConfig}
          />
        </Section>
      ) : null}
      {periodDiff.available_scrap && periodDiff.available_scrap.length > 0 ? (
        <Section title={t("availableScrap")}>
          {periodDiff.available_scrap.map((availableScrap) => (
            <DiffCategory
              key={availableScrap.key}
              diff={availableScrap}
              config={availableScrapAttributesConfig}
            />
          ))}
        </Section>
      ) : null}
      {periodDiff.target_inventory && periodDiff.target_inventory.length > 0 ? (
        <Section title={t("targetInventory")}>
          {periodDiff.target_inventory.map((targetInventory) => (
            <DiffCategory
              key={targetInventory.key}
              diff={targetInventory}
              config={targetInventoryAttributesConfig}
            />
          ))}
        </Section>
      ) : null}
      {periodDiff.target_baskets && periodDiff.target_baskets.length > 0 ? (
        <Section title={t("targetBaskets")}>
          {periodDiff.target_baskets.map((targetBasket) => (
            <DiffCategory
              key={targetBasket.key}
              diff={targetBasket}
              config={targetBasketAttributesConfig}
            />
          ))}
        </Section>
      ) : null}
      {periodDiff.production_groups &&
      periodDiff.production_groups.length > 0 ? (
        <Section title={t("productionGroups")}>
          {periodDiff.production_groups.map((productionGroup) => (
            <DiffCategory
              key={productionGroup.key}
              diff={productionGroup}
              config={productionGroupAttributesConfig}
            />
          ))}
        </Section>
      ) : null}
      {periodDiff.production_items && periodDiff.production_items.length > 0 ? (
        <Section title={t("products")}>
          {periodDiff.production_items.map((productionItem) => (
            <DiffCategory
              key={productionItem.key}
              diff={productionItem}
              config={productionItemAttributesConfig}
            />
          ))}
        </Section>
      ) : null}
    </Stack>
  );
};
